<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h3 class="mb-0">Conto economico & struttura finanziaria</h3>
      <div>
        <button v-if="isEditing" class="btn btn-secondary mr-3" @click="cancel" :disabled="isSaving">Annulla</button>
        <button v-if="isEditing" class="btn btn-primary" @click="confirmSave" :disabled="isSaving">
          <span v-if="isSaving" class="mr-2">
            <div class="spinner-border text-light" style="height: 1rem; width: 1rem; border-width: 0.15em" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </span>
          Salva
        </button>
        <button v-if="!isEditing" class="btn btn-outline-primary" @click="edit">Modifica</button>
      </div>
    </div>
    <div v-if="businessPlan" class="row">
      <!-- BUY -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 custom-card red text-uppercase">
          <div class="card-header">
            <h3>Acquisto</h3>
          </div>
          <div class="card-body text-sm">
            <div v-for="cat in businessPlan.buy" :key="cat.key" class="row no-gutters">
              <div class="col-7">{{ cat.label }}</div>
              <div class="col-5">
                <div class="d-flex justify-content-end text-sm">
                  <span class="mr-1">€</span>
                  <span>{{ numf(getCategoryTotal(cat)) }}</span>
                </div>
              </div>
            </div>

            <div class="row no-gutters align-items-center pt-3 mt-3 border-top">
              <div class="col">Totale costi acquisto</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">
                    <span class="mr-1">€</span>
                    {{ numf(getBuyTotal()) }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SELL -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 custom-card green text-uppercase">
          <div class="card-header">
            <h3>Vendita</h3>
          </div>
          <div class="card-body text-sm">
            <div v-for="cat in businessPlan.sell" :key="cat.key" class="row no-gutters">
              <div class="col-7">{{ cat.label }}</div>
              <div class="col-5">
                <div class="d-flex justify-content-end text-sm">
                  <span class="mr-1">€</span>
                  <span>{{ numf(getCategoryTotal(cat)) }}</span>
                </div>
              </div>
            </div>

            <div class="row no-gutters align-items-center pt-3 mt-3 border-top">
              <div class="col">Margine commerciale</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">
                    <span class="mr-1">€</span>
                    {{ numf(getSellMargin()) }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- COUNTS -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 text-uppercase">
          <div class="card-body text-sm">
            <div class="row no-gutters align-items-center mb-2">
              <div class="col">Utile lordo</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">
                    <span class="mr-1">€</span>
                    {{ numf(getSellTotal()) }}
                  </h3>
                </div>
              </div>
            </div>
            <div class="row no-gutters align-items-center mb-2">
              <div class="col">% ROI</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">{{ getROI() }} %</h3>
                </div>
              </div>
            </div>
            <div class="row no-gutters align-items-center mb-2">
              <div class="col">% ROE</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">{{ getROE() }} %</h3>
                </div>
              </div>
            </div>
            <div class="row no-gutters align-items-center">
              <div class="col">Durata (mesi)</div>
              <div class="col">
                <div class="d-flex justify-content-end">
                  <h3 class="mb-0 text-dark">{{ property.bp_months }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- DATA -->
      <div class="col-md-6 mb-4">
        <div class="card h-100 custom-card gray text-uppercase text-sm">
          <div class="card-header">
            <div class="row w-100 no-gutters align-items-center">
              <div class="col-5">EQUITY PREVISTO</div>
              <div class="col-5">IMPORTO</div>
              <div class="col-2">%</div>
            </div>
          </div>
          <div class="card-body">
            <div class="row no-gutters align-items-center pb-1 mb-1 border-bottom">
              <div class="col-5">Equity</div>
              <div class="col-5 d-flex pr-4">
                <span class="mr-1">€</span>
                <span v-if="!isEditing">{{ numf(property.equity) }}</span>
                <currency-input v-else short v-model="property.equity" />
              </div>
              <div class="col-2">{{ numf(calcEquityPerc(property.equity)) }}</div>
            </div>
            <div class="row no-gutters align-items-center pb-1 mb-1 border-bottom">
              <div class="col-5">Banca</div>
              <div class="col-5 d-flex pr-4">
                <span class="mr-1">€</span>
                <span v-if="!isEditing">{{ numf(property.bank) }}</span>
                <currency-input v-else short v-model="property.bank" />
              </div>
              <div class="col-2">{{ numf(calcEquityPerc(property.bank)) }}</div>
            </div>
            <div class="row no-gutters align-items-center pb-1 mb-1 border-bottom">
              <div class="col-5">Vendite</div>
              <div class="col-5 d-flex pr-4">
                <span class="mr-1">€</span>
                <span v-if="!isEditing">{{ numf(property.sales) }}</span>
                <currency-input v-else short v-model="property.sales" />
              </div>
              <div class="col-2">{{ numf(calcEquityPerc(property.sales)) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { numf } from "@/helpers/helper";
import BpCalcs from "../BusinessPlan/bp-calcs";
let bpCalcs = null;

export default {
  data() {
    return {
      isSaving: false,
      isEditing: false,
      property: null,
      report: null,
      businessPlan: null,
      propertyBk: null
    };
  },

  mounted() {
    this.property = this.$page.props.property;
    this.report = this.$page.props.report;
    this.businessPlan = this.property.business_plan;
    bpCalcs = new BpCalcs(this.businessPlan);
  },

  methods: {
    numf,

    calcEquityPerc(equity) {
      return equity / this.getBuyTotal() * 100;
    },
    getROI() {
      return this.numf((this.getSellTotal() / this.getBuyTotal()) * 100);
    },
    getROE() {
      return this.numf(this.getSellTotal() / this.property.equity);
    },

    getCategoryTotal: cat => bpCalcs.getCategoryTotal(cat),
    getSellMargin: () => bpCalcs.getSellMargin(),
    getSellTotal: () => bpCalcs.getSellTotal(),
    getBuyTotal: () => bpCalcs.getBuyTotal(),

    edit() {
      this.propertyBk = _.cloneDeep(this.property);
      this.isEditing = true;
      $('[data-toggle="tooltip"]').tooltip();
    },
    confirmSave() {
      this.$confirm({
        message: `Salvare il conto economico?`,
        button: {
          no: "No",
          yes: "Si"
        },
        callback: confirm => {
          if (confirm) {
            this.save();
          }
        }
      });
    },
    async save() {
      this.isSaving = true;

      try {
        const response = await this.$inertia.put(`/admin/properties/${this.property.id}/income-statement`, this.property);
      } catch (error) {
        console.log(error);
      }

      this.isSaving = false;
      this.isEditing = false;
    },
    cancel() {
      this.property = _.cloneDeep(this.propertyBk);
      this.isEditing = false;
    }
  }
};
</script>

<style lang="scss">
.bp-reset-btn {
  font-size: 0.8em;
  line-height: 0.5rem;
}
.bp-bg-gray {
  background-color: #f7f7f7;
}
</style>
